import axios from "axios";
import { ftpPreviewResponse } from "@/lib/ftpPreviewData";

// Works unchanged at the domain root ("/") and under a sub-path ("/kidneyassoc/").
// PUBLIC_URL is baked in at build time by the bundler: "" for a root build,
// "/kidneyassoc" for a sub-path build.
//
// TWO INDEPENDENT CONCEPTS - never combine them:
//   BASE_PATH  = where the FRONTEND is served     (build-time: PUBLIC_URL)
//   API_ORIGIN = where the API lives              (build-time: REACT_APP_API_URL)
// If API_ORIGIN is empty the API is assumed same-origin at BASE_PATH + "/api",
// which needs no CORS. If it is set (cross-origin staging), every API and media
// request goes to that host instead.
export const BASE_PATH = (process.env.PUBLIC_URL || "").replace(/\/$/, "");

// REACT_APP_API_URL is the documented build-time name; REACT_APP_BACKEND_URL is accepted
// as a legacy alias. Both are overridden by the RUNTIME value in public/config.js
// (window.__BKA_CONFIG__.apiUrl), which lets a static/FTP host be pointed at an API
// without rebuilding and keeps every hostname out of the compiled bundle.
const runtimeConfig =
  (typeof window !== "undefined" && window.__BKA_CONFIG__) || {};

const runtimeApiUrl = runtimeConfig.apiUrl || "";

// FTP preview mode is deliberately read-only for PUBLIC content. It exists so a static
// Apache/FTP staging host can render the full client-facing site before FastAPI/MongoDB
// infrastructure is available. As soon as apiUrl is configured, the real API wins.
export const FTP_PREVIEW_MODE = Boolean(runtimeConfig.ftpPreviewMode);

export const API_ORIGIN = (
  runtimeApiUrl ||
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_BACKEND_URL ||
  ""
).replace(/\/$/, "");

export const API_BASE = API_ORIGIN ? `${API_ORIGIN}/api` : `${BASE_PATH}/api`;

/** Prefix a STATIC APP asset (shipped in the bundle) so it resolves under a sub-path. */
export const asset = (path) => `${BASE_PATH}${path.startsWith("/") ? path : `/${path}`}`;

/**
 * Resolve any CMS/media image path to a loadable URL. Single source of truth - never
 * concatenate media paths in components.
 *  - "" / null                -> "" (caller renders no <img>)
 *  - https://… or data:       -> unchanged
 *  - /api/media/<id>/file     -> API host (cross-origin) or base path (same-origin)
 *  - /uploads/… , /media/…    -> API host, since the API serves uploaded media
 *  - /brand/… , /assets/… , /static/… -> static app asset, base path
 *  - anything else relative   -> treated as API-relative
 */
export function mediaUrl(path) {
  if (!path || typeof path !== "string") return "";
  const p = path.trim();
  if (!p) return "";
  if (/^(https?:)?\/\//i.test(p) || p.startsWith("data:")) return p;

  const rel = p.startsWith("/") ? p : `/${p}`;
  if (/^\/(brand|assets|static|favicon)/.test(rel)) return `${BASE_PATH}${rel}`;
  if (/^\/(api|uploads|media)\//.test(rel)) return `${API_ORIGIN || BASE_PATH}${rel}`;
  return `${API_ORIGIN || BASE_PATH}${rel}`;
}

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Session tokens are kept here as a fallback for browsers that refuse the cross-site session
// cookie (Safari/iOS, and Chrome with third-party cookies disabled). The cookie is still used
// whenever the browser accepts it.
const ACCESS_KEY = "bka.access_token";
const REFRESH_KEY = "bka.refresh_token";

const store = () => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

export function getStoredToken(key = ACCESS_KEY) {
  return store()?.getItem(key) || "";
}

export function setStoredTokens({ access_token: access, refresh_token: refreshToken }) {
  const s = store();
  if (!s) return;
  if (access) s.setItem(ACCESS_KEY, access);
  if (refreshToken) s.setItem(REFRESH_KEY, refreshToken);
}

export function clearStoredTokens() {
  const s = store();
  if (!s) return;
  s.removeItem(ACCESS_KEY);
  s.removeItem(REFRESH_KEY);
}

api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let refreshing = null;

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const isAuthCall = String(original.url || "").includes("/admin/auth/");
    if (status !== 401 || original._retried || isAuthCall) return Promise.reject(error);

    const storedRefresh = getStoredToken(REFRESH_KEY);
    original._retried = true;
    try {
      refreshing =
        refreshing ||
        api.post("/admin/auth/refresh", storedRefresh ? { refresh_token: storedRefresh } : {});
      const { data } = await refreshing;
      refreshing = null;
      setStoredTokens(data || {});
      return api(original);
    } catch (refreshError) {
      refreshing = null;
      clearStoredTokens();
      return Promise.reject(refreshError);
    }
  }
);

// Short-circuit PUBLIC GET requests in FTP preview mode so the browser does not generate
// expected /api 404 noise on a static-only host. Admin/auth and write operations are never
// simulated here. This is a client-preview safety net, not a production backend.
api.interceptors.request.use((config) => {
  const method = String(config.method || "get").toLowerCase();
  if (FTP_PREVIEW_MODE && !API_ORIGIN && method === "get") {
    const preview = ftpPreviewResponse(config.url, config.params);
    if (preview !== undefined && preview !== null) {
      config.adapter = async () => ({
        data: preview,
        status: 200,
        statusText: "OK",
        headers: { "content-type": "application/json", "x-bka-preview-mode": "ftp-static" },
        config,
        request: null,
      });
    }
  }
  return config;
});

export function apiError(err, fallback = "Something went wrong. Please try again.") {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return (
      detail
        .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
        .filter(Boolean)
        .join(" ") || fallback
    );
  if (typeof detail?.msg === "string") return detail.msg;
  return fallback;
}

export function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

export function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function timeAgo(value) {
  if (!value) return "";
  const diff = (Date.now() - new Date(value).getTime()) / 1000;
  if (Number.isNaN(diff)) return "";
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
  return `${Math.floor(diff / 86400)} days ago`;
}

export function youTubeId(url = "") {
  const value = String(url).trim();
  const patterns = [
    /^(?:https?:\/\/)?(?:www\.|m\.)?youtube(?:-nocookie)?\.com\/watch\?(?:[^#]*&)?v=([A-Za-z0-9_-]{6,})/,
    /^(?:https?:\/\/)?(?:www\.)?youtu\.be\/([A-Za-z0-9_-]{6,})/,
    /^(?:https?:\/\/)?(?:www\.|m\.)?youtube\.com\/shorts\/([A-Za-z0-9_-]{6,})/,
    /^(?:https?:\/\/)?(?:www\.|m\.)?youtube(?:-nocookie)?\.com\/embed\/([A-Za-z0-9_-]{6,})/,
    /^(?:https?:\/\/)?(?:www\.|m\.)?youtube\.com\/live\/([A-Za-z0-9_-]{6,})/,
  ];
  for (const pattern of patterns) {
    const found = value.match(pattern);
    if (found) return found[1];
  }
  return /^[A-Za-z0-9_-]{11}$/.test(value) ? value : null;
}
